import type { ClassificationContent } from '@classification-testing/domain';
import { ClassificationInputTab, ContentType, PromptType } from '@classification-testing/domain';
import { FEATURE_FLAGS } from '@core/domain';
import { useIsFeatureEnabled } from '@core/hooks';
import type { TabItem } from '@soteria/design-system-ve';
import { useMemo } from 'react';

interface UseTabManagementProps {
  activeTab: ClassificationInputTab;
  setActiveTab: (tab: ClassificationInputTab) => void;
  onContentChange: (content: ClassificationContent) => void;
  setPendingFile: (file: File | null) => void;
  clearError: () => void;
  promptType?: PromptType;
}

/**
 * Custom hook to handle tab management logic
 * Reduces complexity in the main component by consolidating tab configuration
 */
export function useTabManagement({ activeTab, setActiveTab, onContentChange, setPendingFile, clearError, promptType }: UseTabManagementProps) {
  const isUsernameEnabled = useIsFeatureEnabled(FEATURE_FLAGS.USERNAME_CLASSIFICATION);

  const isUsernamePromptType = isUsernameEnabled && promptType === PromptType.Username;
  const isClassificationPromptType = promptType === PromptType.Classification;

  const tabs = useMemo(
    (): TabItem[] => [
      { label: 'Enter Text', disabled: isUsernamePromptType },
      { label: 'Upload Audio File', disabled: isUsernamePromptType },
      { label: 'Record Audio', disabled: isUsernamePromptType },
      { label: 'Classify Image', disabled: isUsernamePromptType },
      ...(isUsernameEnabled ? [{ label: 'Classify Username', disabled: isClassificationPromptType }] : []),
    ],
    [isUsernameEnabled, isUsernamePromptType, isClassificationPromptType]
  );

  const tabValues = useMemo(
    () => [
      ClassificationInputTab.TEXT,
      ClassificationInputTab.UPLOAD,
      ClassificationInputTab.RECORD,
      ClassificationInputTab.IMAGE_URL,
      ...(isUsernameEnabled ? [ClassificationInputTab.USERNAME] : []),
    ],
    [isUsernameEnabled]
  );

  const activeTabIndex = tabValues.indexOf(activeTab);

  const handleTabChange = (tabIndex: number) => {
    const method = tabValues[tabIndex];

    if (method) {
      setActiveTab(method);
      onContentChange({
        type: ContentType.TEXT,
        text: '',
      });
      setPendingFile(null);
      clearError();
    }
  };

  return {
    tabs,
    tabValues,
    activeTabIndex,
    handleTabChange,
  };
}
